import React, { useState } from 'react';
import { Snackbar, Button, Stack, FormControl, FormLabel, Input, FormHelperText, Sheet, Textarea, Typography, Box, IconButton, Select, Option, Divider } from '@mui/joy';
import { Switch } from '@mui/joy';
import { Report, InfoOutlined, Remove, ArrowForward, ArrowBack } from '@mui/icons-material';

import LoadingSpinnerComponent from '../LoadingSpinnerComponent';
import RealtimePreview from './RealtimePreview';

import '../../styles/navigation.scss';
import '../../styles/stage.scss';

const networking = require('../../Networking/API');

function ManageStageComponent({ app, handler }: any) {
    const [loading, setLoading] = useState(false);
    const [currentWizardStep, setCurrentWizardStep] = useState(0);

    // Form data states
    const [stageName, setStageName] = useState('');
    const [askEvery, setAskEvery] = useState(1);
    const [versionToggle, setVersionToggle] = useState(true);
    const [message1, setMessage1] = useState('');
    const [message2, setMessage2] = useState('');
    const [message3, setMessage3] = useState('');
    const [question, setQuestion] = useState('');
    const [type, setType] = useState('yn');
    const [questionType, setQuestionType] = useState('1');
    const [items, setItems] = useState([]) as any[];

    // Preview states
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);

    const wizardSteps = [
        'Stage Configuration',
        'First Message',
        'Questions Setup',
        'Final Message',
        'Review'
    ];

    const handleTypeChange = (event: any, newValue: string | null) => {
        if (newValue) {
            setType(newValue);
        }
    }

    const handleQuestionTypeChange = (event: any, newValue: string | null) => {
        // For now we only have two types, so we can toggle between them
        if (newValue) {
            setQuestionType(newValue);
        }
    }

    const addQuestion = (event: any) => {
        event.preventDefault()
        setItems(
            [
                ...items,
                question
            ]
        );
        setQuestion('');
    }

    const nextStep = () => {
        if (currentWizardStep < wizardSteps.length - 1) {
            setCurrentWizardStep(currentWizardStep + 1);
        }
    }

    const prevStep = () => {
        if (currentWizardStep > 0) {
            setCurrentWizardStep(currentWizardStep - 1);
        }
    }

    const canProceedFromStep = () => {
        switch (currentWizardStep) {
            case 0: return stageName.trim() !== '';
            case 1: return message1.trim() !== '';
            case 2: return message2.trim() !== '';
            case 3: return message3.trim() !== '';
            case 4: return true;
            default: return false;
        }
    }

    const saveStage = () => {
        setLoading(true);

        const formData = {
            name: stageName,
            ask_every: askEvery,
            status: 'active',
            questions: items,
            version_toggle: versionToggle === true ? 'true' : 'false',
            first_message: message1,
            second_message: message2,
            final_message: message3,
            type: type,
            questionType: questionType
        };

        networking.saveStage(app, formData).then((stage: any) => {
            setLoading(false);
            handler(true);
        }).catch((error: Error) => {
            setError(error.message);
            setShowError(true);
            setLoading(false);
        });
    }

    function removeQuestion(question: string) {
        setItems(
            items.filter((a: any) =>
                a !== question
            )
        );
    }

    const renderWizardStep = () => {
        switch (currentWizardStep) {
            case 0:
                return (
                    <Box sx={{ bgcolor: 'background.surface', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
                        <Typography level="title-lg" sx={{ mb: 2, textAlign: 'center', marginBottom: 4 }}>
                            Let's setup your moment 🎉
                        </Typography>

                        <Stack spacing={4} sx={{ maxWidth: 500, mx: 'auto', width: '100%' }}>
                            <FormControl>
                                <FormLabel sx={{ fontSize: '12px' }}>
                                    What's your moment identifier?
                                </FormLabel>
                                <Input
                                    value={stageName}
                                    onChange={(e) => setStageName(e.target.value)}
                                    placeholder="Enter stage identifier..."
                                    size='lg'
                                    sx={{ fontSize: '14px' }}
                                />
                                <FormHelperText>
                                    <InfoOutlined sx={{ mr: 0.5, width: 16, height: 16 }} />
                                    This must match your Appinion SDK configuration
                                </FormHelperText>
                            </FormControl>
                            <FormControl>
                                <FormLabel sx={{ fontSize: '12px' }}>
                                    What type of moment is this?
                                </FormLabel>
                                <Select
                                    name="momentType"
                                    defaultValue="feedback"
                                    size="lg"
                                    sx={{ fontSize: '14px' }}
                                    onChange={handleTypeChange}>
                                    <Option value="yn" sx={{ fontSize: '14px' }}>Yes or No</Option>
                                    <Option value="rating" sx={{ fontSize: '14px' }}>Rating</Option>
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel sx={{ fontSize: '12px', mb: 1 }}>How often should we show this moment?</FormLabel>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography>Every</Typography>
                                    <Input
                                        value={askEvery}
                                        onChange={(e) => setAskEvery(Number(e.target.value))}
                                        type="number"
                                        size="md"
                                        sx={{ width: 80 }}
                                    />
                                    <Typography>days</Typography>
                                </Box>
                            </FormControl>
                            <FormControl>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <FormLabel sx={{ fontSize: '12px', mb: 1 }} className='my-auto'>
                                        Display only once per app version
                                    </FormLabel>
                                    <Switch
                                        checked={versionToggle}
                                        onChange={(e) => setVersionToggle(e.target.checked)}
                                    />
                                </Box>
                            </FormControl>
                        </Stack>
                    </Box>
                );

            case 1:
                return (
                    <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography level="title-lg" sx={{ mb: 2, textAlign: 'center' }}>
                            What's your opening message? 👋
                        </Typography>
                        <Typography level="body-sm" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
                            This is the first thing users will see in your feedback prompt
                        </Typography>

                        <Stack spacing={3} sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}>
                            <FormControl>
                                <Textarea
                                    value={message1}
                                    onChange={(e) => setMessage1(e.target.value)}
                                    placeholder="Hi! Are you enjoying your experience with our app?"
                                    minRows={4}
                                    size="lg"
                                    sx={{ fontSize: '14px' }}
                                />
                                <FormHelperText
                                    sx={{ mt: 1, fontSize: '12px' }}>
                                    Keep it friendly and concise. This sets the tone for the entire interaction.
                                </FormHelperText>
                            </FormControl>
                        </Stack>
                    </Box>
                );

            case 2:
                return (
                    <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography level="title-lg" sx={{ mb: 2, textAlign: 'center' }}>
                            Let's gather more details 🤔
                        </Typography>
                        <Typography level="body-sm" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
                            What do you want to ask when users need improvement?
                        </Typography>

                        <Stack spacing={4} sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}>
                            <FormControl>
                                <FormLabel sx={{ fontSize: '12px' }}>Follow-up message</FormLabel>
                                <Textarea
                                    value={message2}
                                    onChange={(e) => setMessage2(e.target.value)}
                                    placeholder="Sorry to hear that. Could you tell us more about your experience?"
                                    minRows={3}
                                    size="lg"
                                    sx={{ fontSize: '14px' }}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel sx={{ fontSize: '12px' }}>
                                    How do you want to ask for feedback?
                                </FormLabel>
                                <Select
                                    name="questionType"
                                    defaultValue="question"
                                    size="lg"
                                    sx={{ fontSize: '14px' }}
                                    onChange={handleQuestionTypeChange}>
                                    <Option value={1} sx={{ fontSize: '14px' }}>Checkbox</Option>
                                    <Option value={2} sx={{ fontSize: '14px' }}>Chip</Option>
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel sx={{ fontSize: '12px', mb: 1 }}>Quick questions (optional)</FormLabel>
                                <FormHelperText
                                    sx={{ mb: 2, fontSize: '12px' }}>
                                    Add preset questions to help users identify issues quickly (up to 3)
                                </FormHelperText>

                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Textarea
                                        value={question}
                                        onChange={(e) => setQuestion(e.target.value)}
                                        placeholder="e.g., App crashes frequently?"
                                        size="lg"
                                        sx={{ flex: 1, fontSize: '14px' }}
                                    />
                                    <Button
                                        onClick={addQuestion}
                                        disabled={!question.trim() || items.length >= 3}
                                        variant="solid"
                                    >
                                        Add
                                    </Button>
                                </Box>

                                {items.length > 0 && (
                                    <Stack spacing={1} sx={{ mt: 2 }}>
                                        {items.map((q: string, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', p: 1, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                                                <Typography
                                                    level="body-xs"
                                                    sx={{ flex: 1 }}>
                                                    {q}
                                                </Typography>
                                                <IconButton
                                                    size="sm"
                                                    color="danger"
                                                    onClick={() => removeQuestion(q)}
                                                >
                                                    <Remove />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </FormControl>
                        </Stack>
                    </Box>
                );

            case 3:
                return (
                    <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography level="title-lg" sx={{ mb: 2, textAlign: 'center' }}>
                            How do you want to thank them? 🙏
                        </Typography>

                        <Stack spacing={3} sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}>
                            <FormControl>
                                <Textarea
                                    value={message3}
                                    onChange={(e) => setMessage3(e.target.value)}
                                    placeholder="Thank you for your valuable feedback! We really appreciate it."
                                    minRows={4}
                                    size="lg"
                                    sx={{ flex: 1, fontSize: '14px' }}
                                />
                                <FormHelperText>
                                    Show appreciation for their time and let them know their input matters.
                                </FormHelperText>
                            </FormControl>
                        </Stack>
                    </Box>
                );

            case 4:
                return (
                    <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography level="title-lg" sx={{ mb: 2, textAlign: 'center' }}>
                            Perfect! Let's review everything ✅
                        </Typography>
                        <Typography level="body-sm" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
                            Take a moment to review your stage configuration
                        </Typography>

                        <Stack spacing={3} sx={{ maxWidth: 700, mx: 'auto', width: '100%' }}>
                            <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'lg' }}>
                                <Typography
                                    level="title-sm" sx={{ mb: 2 }}>
                                    Stage Configuration
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Stack spacing={1}>
                                    <Typography
                                        level='body-sm'>
                                        <strong>Name: </strong>
                                        {stageName}
                                    </Typography>
                                    <Typography
                                        level='body-sm'>
                                        <strong>Type: </strong>
                                        {type === 'yn' ? 'Yes or No' : 'Rating'}
                                    </Typography>
                                    <Typography
                                        level='body-sm'>
                                        <strong>Frequency: </strong>
                                        Every {askEvery} day(s)
                                    </Typography>
                                    <Typography
                                        level='body-sm'>
                                        <strong>Once per version: </strong>
                                        {versionToggle ? 'Yes' : 'No'}
                                    </Typography>
                                </Stack>
                            </Sheet>
                        </Stack>
                    </Box>
                );

            default:
                return null;
        }
    };

    const renderRealtimePreview = () => {
        return (
            <RealtimePreview
                type={type}
                questionType={questionType}
                message1={message1}
                message2={message2}
                message3={message3}
                items={items}
                step={currentWizardStep + 1}
            />
        );
    };

    return (
        <div className='feedback-manager'>
            {
                showError &&
                <Snackbar
                    variant="soft"
                    color="danger"
                    open={showError}
                    onClose={() => setShowError(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowError(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {error}
                </Snackbar>
            }
            {
                loading ?
                    <LoadingSpinnerComponent />
                    :
                    <Box sx={{ bgcolor: 'background.surface' }}>
                        {/* Progress indicator */}
                        <Box sx={{
                            position: 'sticky',
                            top: 0,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            py: 2,
                            px: 3,
                        }}>
                            <Box sx={{ mx: 'auto', width: '50%', backgroundColor: 'transparent' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, maxWidth: 800 }}>
                                    <Typography level="body-sm" color="neutral">
                                        Step {currentWizardStep + 1} of {wizardSteps.length}
                                    </Typography>
                                    <Box sx={{ flex: 1, mx: 2, height: 2, bgcolor: 'background.level2', borderRadius: 1, overflow: 'hidden' }}>
                                        <Box sx={{
                                            width: `${((currentWizardStep + 1) / wizardSteps.length) * 100}%`,
                                            height: '100%',
                                            bgcolor: 'primary.500',
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </Box>
                                    <Typography level="body-sm" color="neutral">
                                        {Math.round(((currentWizardStep + 1) / wizardSteps.length) * 100)}%
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Main content - Two columns layout */}
                        <Box sx={{ px: 3, py: 4 }}>
                            <Box sx={{
                                maxWidth: 1400,
                                mx: 'auto',
                                display: 'flex',
                                gap: 4,
                                alignItems: 'flex-start',
                                flexDirection: { xs: 'column', lg: 'row' }
                            }}>
                                {/* Left column - Wizard */}
                                <Box sx={{ flex: 1, maxWidth: { xs: '100%', lg: '100%' } }}>
                                    {renderWizardStep()}
                                </Box>

                                {/* Right column - Live Preview */}
                                {currentWizardStep !== 4 &&
                                    <Box sx={{
                                        width: { xs: '100%', lg: 400 },
                                        overflowY: 'scroll',
                                        flexShrink: 0,
                                        order: { xs: -1, lg: 1 }
                                    }}>
                                        {renderRealtimePreview()}
                                    </Box>
                                }
                            </Box>
                        </Box>

                        {/* Navigation buttons */}
                        <Box sx={{
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.surface',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            py: 3,
                            px: 3
                        }}>
                            <Box sx={{ maxWidth: 800, mx: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button
                                    variant="plain"
                                    startDecorator={<ArrowBack />}
                                    onClick={prevStep}
                                    disabled={currentWizardStep === 0}
                                    sx={{ visibility: currentWizardStep === 0 ? 'hidden' : 'visible' }}
                                >
                                    Back
                                </Button>

                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    {currentWizardStep < wizardSteps.length - 1 && (
                                        <Button
                                            variant="solid"
                                            endDecorator={<ArrowForward />}
                                            onClick={nextStep}
                                            disabled={!canProceedFromStep()}
                                        >
                                            Continue
                                        </Button>
                                    )}

                                    {currentWizardStep === wizardSteps.length - 1 && (
                                        <Button
                                            variant="solid"
                                            color="primary"
                                            onClick={saveStage}
                                            size="lg"
                                        >
                                            Create Stage
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
            }
        </div >

    );
}

export default ManageStageComponent;